import {
  readFront,
  type WaveGeometry,
  waveGeometry,
  waveTranslate,
  wrapSlide,
} from './ink-wave-geometry'
import {
  afterAnimation,
  readsRtl,
  setMask,
  sigAnimate,
  sigEase,
  sigMs,
  sigNumber,
  signatureMoves,
} from './motion'

/**
 * The ink wave (DESIGN.md "The ink wave"). Pointing at a primary, secondary,
 * outline or ghost button raises a wave of ink through it, corner to corner,
 * and the button stays full while you point; leaving drains it, still moving
 * forward. A press surges it to full; a touch surges and then drains.
 * Keyboard focus has no wave. Never on a disabled, `aria-disabled`, busy or
 * inert button.
 *
 * An `aria-hidden` clip span holds the wave while the ink is up. Inside it the
 * front (the ink, cut by a fixed wave mask) moves by `transform` only, so the
 * compositor runs it with no repaint, and carries a label copy in the flipped
 * colour that moves the opposite way and so stands still. At rest the front
 * drops its mask and transform, so the copy sits on whole pixels. Colours:
 * `--wave-ink` and `--wave-label`, set per variant. Listeners:
 * `ink-wave-listeners.ts`.
 */

const WAVED = new Set(['primary', 'secondary', 'outline', 'ghost'])
/** The tokens' values, for a page without the tokens (a test, a story without the theme). */
const FALLBACK: Record<string, number> = {
  '--sig-dur-wave': 200,
  '--sig-dur-drain': 220,
  '--sig-dur-surge': 140,
}
/** The attributes that end the ink at once when they change. */
const STATES = ['disabled', 'aria-busy', 'aria-disabled']
/** The button's own layout, passed down so the label copy lays out like the label. */
const LAYOUT = ['display', 'flex-direction', 'align-items', 'justify-content', 'gap', 'padding']

interface Wave {
  clip: HTMLSpanElement
  front: HTMLSpanElement
  label: HTMLSpanElement
  g: WaveGeometry
  full: boolean
  /** Where the front rests once the current move ends. */
  at: { u: number; e: number }
  move: Animation | null
  counter: Animation | null
  sheen: HTMLSpanElement | null
  shine: Animation | null
  watch: MutationObserver
  resize: ResizeObserver | null
}

const waves = new WeakMap<HTMLElement, Wave>()
export function waveable(button: HTMLElement): boolean {
  return (
    (button.dataset.variant
      ? WAVED.has(button.dataset.variant)
      : button.classList.contains('ink-wave-host')) &&
    !button.matches(':disabled') &&
    button.getAttribute('aria-disabled') !== 'true' &&
    button.getAttribute('aria-busy') !== 'true' &&
    !button.closest('[inert]')
  )
}

function span(values: Record<string, string>): HTMLSpanElement {
  const el = document.createElement('span')
  el.setAttribute('aria-hidden', 'true')
  for (const [property, value] of Object.entries(values)) el.style.setProperty(property, value)
  return el
}

const inherited = () => Object.fromEntries(LAYOUT.map((property) => [property, 'inherit']))

/** Size an element to a tile and cut it by that tile's mask, placed at its own corner. */
function tile(el: HTMLElement, g: WaveGeometry, image: string) {
  const size = `${g.tileWidth}px ${g.tileHeight}px`
  el.style.width = `${g.tileWidth}px`
  el.style.height = `${g.tileHeight}px`
  setMask(el, { Image: image, Size: size, Repeat: 'no-repeat', Position: '0 0' })
}

/** The front at rest and full: no mask, no transform, the button's own box. */
function toRest(wave: Wave) {
  const { front, label } = wave
  front.style.width = '100%'
  front.style.height = '100%'
  setMask(front, null)
  front.style.transform = ''
  label.style.transform = ''
}

function place(wave: Wave, u: number, e: number) {
  wave.front.style.transform = waveTranslate(wave.g, u, e)
  wave.label.style.transform = waveTranslate(wave.g, u, e, -1)
}

function stopMove(wave: Wave) {
  wave.move?.cancel()
  wave.counter?.cancel()
  wave.move = null
  wave.counter = null
}

function stopSheen(wave: Wave) {
  wave.shine?.cancel()
  wave.shine = null
  wave.sheen?.remove()
  wave.sheen = null
}

function drop(button: HTMLElement, wave: Wave) {
  stopMove(wave)
  stopSheen(wave)
  wave.watch.disconnect()
  wave.resize?.disconnect()
  wave.clip.remove()
  if (waves.get(button) === wave) waves.delete(button)
}

/** While the pointer rests, a soft sheen behind the same front crosses the ink once every 2.6s. */
function startSheen(wave: Wave) {
  if (wave.sheen || !wave.full || !signatureMoves() || !wave.clip.isConnected) return
  const { g } = wave
  const sheen = span({
    position: 'absolute',
    top: '0',
    left: '0',
    'pointer-events': 'none',
    background: 'var(--wave-label, var(--background))',
    opacity: '0.2',
  })
  tile(sheen, g, g.sheen)
  sheen.style.transform = waveTranslate(g, g.empty, 0)
  wave.front.insertBefore(sheen, wave.label)
  wave.sheen = sheen
  const slide = 1.2 * g.lambdaA
  wave.shine = sheen.animate(
    [
      { transform: waveTranslate(g, g.empty, 0), easing: sigEase('--sig-ease-wave') },
      { transform: waveTranslate(g, g.beyond, slide), offset: 0.7 },
      { transform: waveTranslate(g, g.beyond, slide) },
    ],
    { duration: sigMs('--sig-dur-glow', 2600), iterations: Number.POSITIVE_INFINITY, delay: 160 },
  )
}

/** Cover the button, border included, and size the copy to its exact box. The border is
 * read: the engine snaps 1px to whole device pixels (0.8px at 125%). */
function fitToButton(clip: HTMLElement, label: HTMLElement, button: HTMLElement) {
  const { width, height, borderTopWidth } = getComputedStyle(button)
  clip.style.inset = `-${borderTopWidth}`
  Object.assign(label.style, { width, height, borderWidth: borderTopWidth })
}

function create(button: HTMLElement): Wave {
  const clip = span({
    position: 'absolute',
    'z-index': '1',
    'pointer-events': 'none',
    overflow: 'hidden',
    'border-radius': 'inherit',
    ...inherited(),
  })
  const front = span({
    position: 'absolute',
    top: '0',
    left: '0',
    background: 'var(--wave-ink, var(--foreground))',
    ...inherited(),
  })
  front.dataset.signature = 'ink-wave'
  // Read before the clip goes in, so the read forces no fresh style and
  // layout pass. The clip covers the border too: the button's size.
  const ltr = !readsRtl(button)
  const size = sigNumber('--sig-wave-size', 6)
  const g = waveGeometry(button.offsetWidth, button.offsetHeight, ltr, size)
  const label = span({
    position: 'absolute',
    top: '0',
    left: '0',
    border: 'solid transparent',
    color: 'var(--wave-label, var(--background))',
    ...inherited(),
  })
  fitToButton(clip, label, button)
  front.appendChild(label)
  clip.appendChild(front)
  button.appendChild(clip)
  const wave: Wave = {
    clip,
    front,
    label,
    g,
    full: false,
    at: { u: g.empty, e: 0 },
    move: null,
    counter: null,
    sheen: null,
    shine: null,
    // A button that turns busy or disabled under the ink shows its own state at once.
    watch: new MutationObserver(() => {
      if (!waveable(button)) drop(button, wave)
    }),
    resize: null,
  }
  tile(wave.front, wave.g, wave.g.wave)
  place(wave, g.empty, 0)
  wave.watch.observe(button, { attributes: true, attributeFilter: STATES })
  if (typeof ResizeObserver === 'function') {
    // A font arriving or a label changing redraws the front; a draining wave is dropped.
    wave.resize = new ResizeObserver(() => {
      const { offsetWidth: w, offsetHeight: h } = clip
      if (!clip.isConnected || (w === wave.g.width && h === wave.g.height)) return
      stopMove(wave)
      stopSheen(wave)
      if (!wave.full) return drop(button, wave)
      wave.g = waveGeometry(w, h, ltr, size)
      wave.at = { u: wave.g.full, e: 0 }
      fitToButton(clip, label, button)
      toRest(wave)
      startSheen(wave)
    })
    wave.resize.observe(clip)
  }
  waves.set(button, wave)
  return wave
}

/** Raise the ink to full, or drain it, from wherever it is now. */
export function inkTo(button: HTMLElement, full: boolean, durationToken: string) {
  let wave = waves.get(button)
  const live = !!wave && wave.clip.parentElement === button
  if (wave && !live) drop(button, wave)
  if (full && !waveable(button)) return
  if (!full && !live) return
  if (!wave || !live) wave = create(button)
  const { g } = wave
  stopSheen(wave)
  if (full && !wave.full) {
    // The label copy is taken each time the ink rises, so it always matches the label.
    const clip = wave.clip
    const copies = [...button.childNodes].filter((n) => n !== clip).map((n) => n.cloneNode(true))
    wave.label.replaceChildren(...copies)
    for (const withId of wave.label.querySelectorAll('[id]')) withId.removeAttribute('id')
  }
  // Mid-move, the front is read back from the running animation; at rest it is known.
  const from =
    wave.move?.playState === 'running'
      ? readFront(g, getComputedStyle(wave.front).transform)
      : wave.at
  stopMove(wave)
  const to = full ? g.full : g.empty
  const share = Math.min(1, Math.abs(to - from.u) / (g.full - g.empty))
  // The crests slide along the front as it moves.
  const e = from.e + (full ? 1 : -1) * g.lambdaA * sigNumber('--sig-wave-drift', 1.2) * share
  wave.full = full
  wave.at = { u: to, e: wrapSlide(e, g.lambdaA) }
  tile(wave.front, wave.g, wave.g.wave)
  place(wave, to, wave.at.e)
  const current = wave
  const settle = () => {
    stopMove(current)
    if (!current.full) return drop(button, current)
    toRest(current)
    startSheen(current)
  }
  if (!signatureMoves() || share < 0.02) return settle()
  const timing = {
    duration: Math.max(60, sigMs(durationToken, FALLBACK[durationToken] ?? 300) * share),
    easing: sigEase('--sig-ease-wave'),
  }
  // The front and the label copy move as mirror images, on one timing.
  const glide = (el: HTMLElement, sign: number) =>
    sigAnimate(
      el,
      [
        { transform: waveTranslate(g, from.u, from.e, sign) },
        { transform: waveTranslate(g, to, e, sign) },
      ],
      timing,
    )
  wave.counter = glide(wave.label, -1)
  const move = glide(wave.front, 1)
  wave.move = move
  afterAnimation(move, () => {
    if (current.move === move && move.playState === 'finished') settle()
  })
}
